import React from 'react'
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Plus, Tag, Upload } from 'lucide-react';
import Form from 'react-bootstrap/Form';
import { useState } from 'react';
import AddProductApi from "../../../services/products/createProducts.js"
import { toast } from 'react-toastify';
import style from "./AddItemModal.module.scss"
import handleErrorToast from '../../../utils/handleErrorToasts.js';
import { useEffect, useRef } from 'react';
import ITEM_TOASTS from '../../../utils/constants/toastMesages/itemToastMessage.js';
import fetchCategories from '../../../services/categories/fetchCategories.js';
import createCategory from '../../../services/categories/createCategory.js';

/**
 * Component to render a modal for adding a new item.
 *
 * @component
 * @param {Object} props - Props passed to the component
 * @param {boolean} props.show - Controls the visibility of the modal
 * @param {Function} props.onHide - Function to hide/close the modal
 * @returns {JSX.Element} A modal dialog to add an item
 *
 * @example
 * <AddItemModal show={true} onHide={() => setShow(false)} />
 *
 * @author Susovan
 */
function AddItemModal({show, onHide}) {

  //As its handled by the AddItem component this will directly affect and update the database
const [productDetails, setProductDetails] = useState({
  prodName: "",
  prodPrice: null,
  prodCategoryId: "",
  prodCategoryName: "",
  quantity: "",
  description: "",
  imageUrl: "",
  veg: true,
  file: null,
  previewUrl: null,
})

const fileInputRef = useRef(null);

const handleClick = () => {
  fileInputRef.current.click();
}

const handleFileChange = (event) => {
  const file = event.target.files[0];
  const previewUrl = URL.createObjectURL(file);
  if (file) {
    setProductDetails({ ...productDetails,file: file, previewUrl: previewUrl });
  } 
} 

const [categories, setCategories] = useState([]);
const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
const [newCategoryName, setNewCategoryName] = useState("");
const [isCreatingCategory, setIsCreatingCategory] = useState(false);

//Getting item types from .env file as array
const itemTypes = JSON.parse(process.env.REACT_APP_PRODUCT_CATEGORIES_JSON);

// Function to handle creating a new category
const handleCreateNewCategory = async () => {
  if (!newCategoryName.trim()) {
    toast.error("Category name is required");
    return;
  }

  setIsCreatingCategory(true);
  try {
    const response = await createCategory({ name: newCategoryName.trim() });
    const newCategory = response.data;
    
    // Add the new category to the categories list
    setCategories(prev => [...prev, newCategory]);
    
    // Auto-select the newly created category
    setProductDetails({
      ...productDetails,
      prodCategoryId: newCategory.id,
      prodCategoryName: newCategory.name,
    });
    
    // Reset the new category input
    setNewCategoryName("");
    setShowNewCategoryInput(false);
    
    toast.success("Category created successfully!");
  } catch (error) {
    handleErrorToast(error, "Failed to create category");
  } finally {
    setIsCreatingCategory(false);
  }
};

// Function to handle category selection change
const handleCategoryChange = (value) => {
  if (value === "add_new") {
    setShowNewCategoryInput(true);
  } else {
    setShowNewCategoryInput(false);
    const selectedCategory = categories.find((cat) => cat.id === value);
    setProductDetails({
      ...productDetails,
      prodCategoryId: value,
      prodCategoryName: selectedCategory?.name || "",
    });
  }
};

//Api handler for sendig and saving data to backend
const addProduct = async () =>{
  // Prevent adding product if currently creating a category
  if (isCreatingCategory) {
    toast.error("Please wait for category creation to complete");
    return;
  }
  
  // Check if category is selected or being created
  if (!productDetails.prodCategoryId && !showNewCategoryInput) {
    toast.error("Please select a category");
    return;
  }
  
  try{
   const res = await AddProductApi(productDetails);
    toast.success(ITEM_TOASTS.ITEM_ADDED);
    onHide()
    setProductDetails({})
    // Reset category creation states
    setShowNewCategoryInput(false);
    setNewCategoryName("");
  }
  catch(error){
    toast.error("Please add all details")
  }
}

  const getCategorises = async () => {
    try {
      const categories = await fetchCategories();
      console.log("Fetched categories:", categories);
      setCategories(categories.data);
    } catch (error) {
      handleErrorToast(error, "Failed to fetch categories");
    }
  }

  useEffect(() => {
    getCategorises();
  }, [show, onHide]);

  return (
    <Modal
    show={show}
    onHide={onHide}
    size="lg"
    aria-labelledby="contained-modal-title-vcenter"
    centered
  >
    <Modal.Header closeButton>
      <Modal.Title id="contained-modal-title-vcenter">
        Add Product
      </Modal.Title>
    </Modal.Header>
    <Modal.Body className={`px-5 ${style.modalScrollableBody}`}>
  <div className="row">
    <div className="col-md-7 d-flex flex-column">
      <div className="mb-3">
        <label htmlFor="productName" className="form-label">Product Name</label>
        <input
          value={productDetails.prodName}
          onChange={(e) =>
            setProductDetails({ ...productDetails, prodName: e.target.value })
          }
          type="text"
          className="form-control"
          id="productName"
          placeholder="Product Name"
          onKeyDown={(e) => {if (e.key === "Enter") addProduct()}}
        />
        { !productDetails.prodName &&
          <span className='text-muted' style={{fontSize:"12px"}}>Product name is required!*</span>
        }
      </div>

      <div className='d-flex gap-3'>
      <div className="mb-3">
        <label htmlFor="productPrice" className="form-label">Price</label>
        <input
          value={productDetails.prodPrice}
          onChange={(e) =>
            setProductDetails({ ...productDetails, prodPrice: e.target.value })
          }
          type="number"
          className="form-control"
          id="productPrice"
          placeholder="Price in ₹"
          onKeyDown={(e) => {if (e.key === "Enter") addProduct()}}
        />
        { !productDetails.prodPrice &&
          <span className='text-muted' style={{fontSize:"12px"}}>Product price is required!*</span>
        }
      </div>

      <div className="mb-3">
        <label htmlFor="productImage" className="form-label">Image Link</label>
        <input
          type="text"
          className="form-control"
          id="productImage"
          placeholder="Image URL"
          value={productDetails.imageUrl}
          onChange={(e) => setProductDetails({ ...productDetails, imageUrl: e.target.value })}
        />
      </div>
      </div>

      <div className='d-flex gap-3'>
      <div className="mb-3">
        <label htmlFor="productCategory" className="form-label">Category</label>
        <Form.Select
          value={showNewCategoryInput ? "add_new" : productDetails.prodCategoryId}
          onChange={(e) => handleCategoryChange(e.target.value)}
          disabled={isCreatingCategory}
        >
          <option value="">Select category</option>
          {categories.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
          <option value="add_new">+ Add New Category</option>
        </Form.Select>
        
        {showNewCategoryInput && (
          <div className="mt-3 p-3 border rounded bg-light">
            <div className="d-flex align-items-center gap-2 mb-2">
              <Tag size={16} className="text-primary" />
              <label className="form-label mb-0">New Category Name</label>
            </div>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Enter category name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                disabled={isCreatingCategory}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateNewCategory();
                  }
                }}
              />
              <Button
                variant="primary"
                onClick={handleCreateNewCategory}
                disabled={isCreatingCategory || !newCategoryName.trim()}
                size="sm"
              >
                {isCreatingCategory ? "Creating..." : "Add"}
              </Button>
            </div>
          </div>
        )}
        
        { !productDetails.prodCategoryId && !showNewCategoryInput &&
          <span className='text-muted' style={{fontSize:"12px"}}>Product category is required!*</span>
        }
      </div>
      <div className='mb-3'>
        <label htmlFor="" className='form-label'>Stock Quantity</label>
        <input
          value={productDetails.quantity}
          onChange={(e) =>
            setProductDetails({ ...productDetails, quantity: e.target.value })
          }
          type="number"
          className="form-control"
          id="productQuantity"
          placeholder="Add Quantity"
          onKeyDown={(e) => {if (e.key === "Enter") addProduct()}}
        />
        { !productDetails.quantity &&
          <span className='text-muted' style={{fontSize:"12px"}}>Product quantity is required!*</span>
        }
      </div>
      </div>

      <div className="mb-3">
        <label htmlFor="productType" className="form-label">Type</label>
        <Form.Select
          value={productDetails.veg ? "veg" : "non-veg"}
          onChange={(e) => setProductDetails({ ...productDetails, veg: e.target.value === "veg" })}
        >
          <option value="veg">Vegetarian</option>
          <option value="non-veg">Non-Vegetarian</option>
        </Form.Select>
      </div>

      <div className="mb-3">
        <label htmlFor="productDescription" className="form-label">Description</label>
        <textarea
          className="form-control"
          id="productDescription"
          rows="3"
          value={productDetails.description}
          onChange={(e) => setProductDetails({ ...productDetails, description: e.target.value })}
        ></textarea>
      </div>
    </div>
    <input type="file" className='d-none' ref={fileInputRef} onChange={handleFileChange} />
    <div className={`${style.plusIconDiv} col-md-5 d-flex justify-content-center pt-4 align-items-center`}>
      <div
        className={`border rounded d-flex justify-content-center align-items-center ${style.plusIcon}`} onClick={handleClick}
      >
        {productDetails.previewUrl ? (
          <img 
            src={productDetails.previewUrl} 
            alt="Preview" 
            style={{ height: '100%', width: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Upload size={60} style={{ fontSize: "4rem" }} />
        )}
      </div>
    </div>
  </div>
  </Modal.Body>

    <Modal.Footer>
      <Button variant='danger' onClick={onHide}>Close</Button>
      <Button onClick={()=>addProduct()}>Save</Button>
    </Modal.Footer>
  </Modal>
  
  )
}

export default AddItemModal